import React, { useState } from 'react';
import { useDrop } from 'react-dnd';
import { Stage, Layer, Group, Rect } from 'react-konva';
import { DragAndDropTypesEnum, PLATFORM_THEME_COLORS } from '@thinknet-gmbh/platform-common-ui';

import { useMoveRecordEntry } from 'src/hooks/gantts-records/move-gantt-record-entry.hook';
import { useAddTimeStripeToRecordEntry } from 'src/hooks/gantts-records/add-gantt-record.hook';
import { useGetHolidaySubgroupsForGantt } from 'src/hooks/holiday-manager/get-holiday-subgroup-by-location-id.hook';
import { useGetCurrentGantt } from 'src/hooks/gantts/get-current-gantt.hook';

import { InfoLayer } from './components/info-layer';
import { DataLayer } from './components/data-layer';
import { GridLayer } from './components/grid-layer/';

import { GanttCalendarWrapper } from './gantt-view.styles';
import { getEntryByPointPosition } from './gantt-view.helpers';
import { DateColumnsLayer } from './components/date-columns-layer';
import { StatusColumnsLayer } from './components/status-columns-layer/';
import {
  COLUMN_HEIGHT,
  COLUMN_WIDTH,
  DATE_COLUMN_WIDTH,
  HEADER_ROW_HEIGHT,
} from './gantt-view.constants';
import { IGanttView } from './gantt-view.interfaces';
import { getDateViewInfoFromViewRange, getGanttGridInfo } from './gantt-view.helpers';
import { GANTT_VIEW_POPOVER_TYPES } from './components/gantt-popover/gantt-popover.constants';
import { useAutoScrollToDate } from './gantt-view.hooks';

const GanttView: React.FC<IGanttView> = ({
  t,
  ganttData,
  ganttControl,
  viewRange,
  containerWidth,
  colorSchema,
  setDateBarLeftOffset,
  dateBarLeftOffset,
  setActivePopoverInfo,
  setConflictModal,
}) => {
  const { highlightProjectIds = [] } = ganttControl;
  const stageRef = React.createRef<any>();
  const [headerTopOffset, setHeaderTopOffset] = useState(0);
  const { holidaySubGroups } = useGetHolidaySubgroupsForGantt();

  const { gantt } = useGetCurrentGantt();
  const { viewColumnDateFormat, dateMapByDate, dateValues } = getDateViewInfoFromViewRange(
    viewRange,
    gantt.weekends,
    ganttControl,
    holidaySubGroups
  );
  const scrollContainerRef = useAutoScrollToDate(ganttControl, dateMapByDate);
  const { fullWidth, fullHeight } = getGanttGridInfo(ganttData, containerWidth, dateValues);

  const moveRecordEntry = useMoveRecordEntry();

  const addTimeStripeEntry = useAddTimeStripeToRecordEntry();
  const moveRecordEntryHandler = ({ pointX, pointY }: any, { sourceId, groupId }: any) => {
    const { employee, date } = getEntryByPointPosition({ pointX, pointY, ganttData, dateValues });

    if (employee && date) {
      const payload = {
        groupId,
        sourceId,
        employeeId: employee._id,
        ganttId: employee.ganttId,
        dateString: date.dateInFormat,
        companyId: employee.companyId,
      };

      const moveRecordEntryCallback = ({ confirmed = true }: any = {}) =>
        moveRecordEntry({ variables: { payload: { ...payload, confirmed } } })
          .then(({ data: { moveRecordEntry: conflicts } }) => {
            if (conflicts.length) {
              setConflictModal({
                conflicts,
                onConfirm: () => {
                  moveRecordEntryCallback();
                  setConflictModal(false);
                },
              });
            }
          })
          .catch();

      moveRecordEntryCallback({ confirmed: false });

      return true;
    }

    return false;
  };
  const [, drop] = useDrop(
    () => ({
      accept: DragAndDropTypesEnum.TIME_STRIPE,
      drop: ({ _id: timeStripeId }: any, instance) => {
        const { x: mouseX = 0, y: mouseY = 0 } = instance.getClientOffset() || {};
        const { x: startX, y: startY } = stageRef.current.content.getBoundingClientRect();
        const [pointX, pointY] = [mouseX - startX, mouseY - startY];
        const { employee, date } = getEntryByPointPosition({
          pointX,
          pointY,
          ganttData,
          dateValues,
        });

        if (employee && date) {
          const payload = {
            timeStripeId,
            employeeId: employee._id,
            ganttId: employee.ganttId,
            dateString: date.dateInFormat,
            companyId: employee.companyId,
          };

          const addTimeStripeEntryCallback = ({ confirmed = true }: any = {}) => {
            addTimeStripeEntry({ variables: { payload: { ...payload, confirmed } } }).then(
              ({ data: { addTimeStripeToRecordEntry: conflicts } }) => {
                if (conflicts.length) {
                  setConflictModal({
                    conflicts,
                    onConfirm: () => {
                      addTimeStripeEntryCallback();
                      setConflictModal(false);
                    },
                  });
                }
              }
            );
          };

          addTimeStripeEntryCallback({ confirmed: false });
        }
      },
    }),
    [dateValues, ganttData]
  );

  return (
    <GanttCalendarWrapper
      ref={node => {
        if (node) scrollContainerRef.current = node;

        drop(node);
      }}
      onScroll={(event: any) => {
        const { top, left } = event.currentTarget.getClientRects()[0];
        const { y, x } = event.currentTarget.firstChild.getClientRects()[0];

        setActivePopoverInfo(null);
        setDateBarLeftOffset(Math.abs(x - left));
        setHeaderTopOffset(Math.abs(top - y));
      }}
    >
      <Stage
        ref={stageRef}
        width={fullWidth}
        height={fullHeight}
        fill={PLATFORM_THEME_COLORS.primary.white}
        onDblClick={event => {
          const { offsetX: pointX, offsetY: pointY } = event.evt;
          const {
            record: target,
            employee,
            date,
          } = getEntryByPointPosition({
            pointX,
            pointY,
            ganttData,
            dateValues,
          });

          if (target) {
            setActivePopoverInfo({
              entryView: {
                x: employee.index * COLUMN_WIDTH + DATE_COLUMN_WIDTH - dateBarLeftOffset,
                y: date.index * COLUMN_HEIGHT + HEADER_ROW_HEIGHT - headerTopOffset,
                height: COLUMN_HEIGHT,
                width: COLUMN_WIDTH,
              },
              type: GANTT_VIEW_POPOVER_TYPES.dayEntriesInfo,
              target,
            });
          }
        }}
      >
        <Layer>
          {/* Gantt white background */}
          <Rect
            x={0}
            y={0}
            height={fullHeight}
            width={fullWidth}
            fill={PLATFORM_THEME_COLORS.primary.white}
          />
          <StatusColumnsLayer
            colorSchema={colorSchema}
            fullWidth={fullWidth}
            ganttData={ganttData}
            dateValues={dateValues}
          />

          <GridLayer
            ganttData={ganttData}
            dateValues={dateValues}
            fullWidth={fullWidth}
            fullHeight={fullHeight}
          />

          {/* Gantt body dark background on search */}
          <Rect
            x={0}
            y={0}
            width={fullWidth}
            height={fullHeight}
            visible={!!highlightProjectIds.length}
            fill={PLATFORM_THEME_COLORS.grayVariations.dark}
            opacity={0.2}
          />
        </Layer>

        <DataLayer
          ganttData={ganttData}
          ganttControl={ganttControl}
          dateMapByDate={dateMapByDate}
          headerTopOffset={headerTopOffset}
          dateBarLeftOffset={dateBarLeftOffset}
          moveRecordEntry={moveRecordEntryHandler}
          setPopoverInfo={setActivePopoverInfo}
        />

        <InfoLayer ganttData={ganttData} dateMapByDate={dateMapByDate} />

        <Layer>
          <Group listening={false} x={dateBarLeftOffset}>
            <DateColumnsLayer
              colorSchema={colorSchema}
              fullHeight={fullHeight}
              dateValues={dateValues}
              viewFormat={viewColumnDateFormat}
            />
          </Group>
        </Layer>
      </Stage>
    </GanttCalendarWrapper>
  );
};

export default GanttView;
